import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import Storage from "@aws-amplify/storage";
import { isEqual } from "lodash";
import { Prompt } from "react-router-dom";

// Components
import { Rnd } from "react-rnd";
import { Formik, Form, Field } from "formik";
import {
  Container,
  ButtonToolbar,
  ButtonGroup,
  ListGroup,
  FormGroup,
  FormControl,
  Row,
  Spinner,
  Button,
  Alert,
} from "react-bootstrap";
import SettingItem from "./SettingItem";
import ImageUpload from "./ImageUpload";
import BackgroundImageUpload from "./BackgroundImageUpload";
import FontSelection from "./FontSelection";
import { getRestaurant } from "../actions/Menu";
// Actions
import {
  TOGGLE_CREATE_MENU,
  TOGGLE_UPDATE_MENU,
  UPDATE_RESTAURANT_SUCCESS,
  UPDATE_RESTAURANT_ERROR,
  CREATE_RESTAURANT_ERROR,
  CREATE_RESTAURANT_SUCCESS,
} from "../actions/ActionTypes";
import {
  handleUpdateRestaurant,
  handleCreateRestaurant,
  createDefaultRestaurant,
} from "../actions/Menu";

// Constants
import { IMAGES } from "../constants";

// Schema for yup
const validationSchema = Yup.object().shape({
  restaurantName: Yup.string()
    .min(3, "*Restaurant Name must have at least 3 characters")
    .max(50, "*Restaurant Name can't be longer than 20 characters")
    .required("*Restaurant Name is required"),
});

const PreviewRestaurantBackgroundImage = ({
  restaurantSetting,
  previewBackgroundImage,
  nameStyle,
  descriptionTextStyle,
  dateStyle,
}) => {
  return (
    <div
      className="preview-background-image"
      style={{
        backgroundImage: `url(${
          restaurantSetting?.displayBackground
            ? previewBackgroundImage || IMAGES.APP_DEFAULT_BACKGROUND
            : IMAGES.APP_DEFAULT_BACKGROUND
        })`,
      }}
    >
      <Container className="text-center">
        <p className="restaurant-name text-truncate" style={nameStyle}>
          {restaurantSetting.name}
        </p>
        <p
          className="restaurant-intro text-truncate"
          style={descriptionTextStyle}
        >
          {restaurantSetting.introduction}
        </p>
        {restaurantSetting.displayDate && (
          <p className="restaurant-date" style={dateStyle}>
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        )}
      </Container>
    </div>
  );
};

const ResizablePreviewModal = ({ isDisplay, children, handleClose }) => {
  const [resizeSize, setResizeSize] = useState(320);
  const listDevices = [
    { name: "iPhone 5/SE", width: 320 },
    { name: "iPhone 6/7/8", width: 375 },
    { name: "iPhone 6/7/8 Plus", width: 414 },
    { name: "iPhone X", width: 375 },
    { name: "iPad", width: 768 },
    { name: "iPad Mini", width: 768 },
    { name: "iPad Pro", width: 1024 },
    { name: "Moto G4", width: 360 },
    { name: "Pixel 2", width: 411 },
    { name: "Pixel 2XL", width: 411 },
    { name: "Surface Duo", width: 540 },
    { name: "Galaxy S5", width: 360 },
    { name: "Galaxy Note 3", width: 360 },
    { name: "Galaxy Fold", width: 280 },
    { name: "Laptop with HiDPI screen", width: 1440 },
    { name: "Laptop with MDPI screen", width: 1280 },
  ];
  const selectDevicesElement = useRef(null);

  const onSelectDevice = (event) => {
    setResizeSize(event.target.value);

    if (selectDevicesElement.current) {
      // Removing focus state after select
      selectDevicesElement.current.blur();
    }
  };

  return (
    <div
      className="preview-modal-mask"
      style={{
        display: isDisplay ? "block" : "none",
      }}
    >
      <Rnd
        size={{ width: resizeSize }}
        default={{
          x: 150,
          y: 200,
          width: 320,
          height: "auto",
        }}
        minWidth={280}
        bounds="window"
        onResize={(e, val, ref) => {
          setResizeSize(ref.offsetWidth);
        }}
        className="preview-modal"
        enableResizing={{
          top: false,
          right: true,
          bottom: false,
          left: true,
          topRight: false,
          bottomRight: false,
          bottomLeft: false,
          topLeft: false,
        }}
        style={{ background: "white" }}
      >
        <div className="preview-modal-body">{children}</div>
        <div className="preview-modal-footer">
          <FormControl
            onChange={onSelectDevice}
            as="select"
            ref={selectDevicesElement}
          >
            {listDevices.map((device) => (
              <option key={device.name} value={device.width}>
                {device.name}
              </option>
            ))}
          </FormControl>
          <p className="preview-modal-text mb-3 mt-3">
            <strong>Width: </strong>
            {resizeSize}
          </p>
          <div className="preview-modal-resizer" />
          <Button onClick={handleClose}>Close</Button>
        </div>
      </Rnd>
    </div>
  );
};

const RestaurantSettingInputs = (props) => {
  const { handleSettingChange, setFieldValue, touched, errors } = props;

  const handleNameChange = (e) => {
    const { value } = e.target;

    handleSettingChange("name", value);
    setFieldValue("restaurantName", value);
  };

  const handleIntroduceChange = (e) => {
    const { value } = e.target;

    handleSettingChange("introduction", value);
    setFieldValue("restaurantDescription", value);
  };

  return (
    <>
      {/* Restaurant name field */}
      <Field name="restaurantName">
        {({ field }) => (
          <FormGroup
            controlId="restaurantName"
            className="setting-form-group setting-form-group-name w-50"
          >
            <FormControl
              type="text"
              value={field.value}
              onChange={handleNameChange}
              placeholder="Enter Restaurant Name"
              isValid={touched.restaurantName && !errors.restaurantName}
              isInvalid={touched.restaurantName && errors.restaurantName}
              name="restaurantName"
              className="setting-input"
              data-testid="restaurant-name-input"
            />
            <FormControl.Feedback type="invalid">
              {errors.restaurantName}
            </FormControl.Feedback>
          </FormGroup>
        )}
      </Field>

      {/* Restaurant description field */}
      <Field name="restaurantDescription">
        {({ field }) => (
          <FormGroup
            controlId="restaurantDescription"
            className="setting-form-group setting-form-group-textarea mt-3"
          >
            <FormControl
              as="textarea"
              rows="10"
              type="text"
              value={field.value}
              onChange={handleIntroduceChange}
              name="restaurantIntro"
              placeholder="Introduction..."
              className="setting-input setting-input-textarea"
              data-testid="introduction-input"
            />
          </FormGroup>
        )}
      </Field>
    </>
  );
};

RestaurantSettingInputs.propTypes = {
  handleSettingChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

const RestaurantSettingInputsMemo = memo(RestaurantSettingInputs);

export const GeneralSettings = ({
  restaurant,
  dispatch,
  auth,
  history,
  getRestaurant,
}) => {
  const getRestaurantCallback = useCallback(getRestaurant, [getRestaurant]);
  const [loadingStatus, setLoadingStatus] = useState({
    getRestaurant: false,
    postRestaurant: false,
  });
  const [restaurantSetting, setRestaurantSetting] = useState(
    createDefaultRestaurant()
  );
  const [error, setError] = useState({
    getRestaurant: "",
    postRestaurant: "",
    preventBookmarkUpdate: false,
  });

  const [previewBackgroundImage, setPreviewBackgroundImage] = useState(null);
  const [togglePreviewModal, setTogglePreviewModal] = useState(false);

  useEffect(
    function loadRestaurant() {
      const getRestaurantByUserGroup = async (auth) => {
        const { user, userGroup } = auth;
        if (user && user.id && userGroup) {
          try {
            // Set loading status
            setLoadingStatus((prevState) => ({
              ...prevState,
              getRestaurant: true,
            }));

            await getRestaurantCallback(userGroup);
          } catch (e) {
            setError((prevState) => ({
              ...prevState,
              getRestaurant: e.message,
            }));
          } finally {
            // Reset loading status
            setLoadingStatus((prevState) => ({
              ...prevState,
              getRestaurant: false,
            }));
          }
        }
      };

      getRestaurantByUserGroup(auth);
    },
    [auth, getRestaurantCallback]
  );

  useEffect(
    function updateRestaurantSetting() {
      setRestaurantSetting(restaurant);
    },
    [restaurant]
  );

  /**
   * Update the restaurant setting
   * If user upload new logo-image, do upload logo-image to server here
   */
  const handleSubmit = async (values) => {
    const {
      id,
      displayDate = false,
      displayLogo = false,
      displayCurrencySymbol = false,
      logoImage,
      backgroundImage,
      displayBackground = false,
      displayLogoInQRCode = false,
      restaurantName,
      dateText,
      descriptionText,
      bookmarksEnabled,
      loadSectionOnly,
      zoomDisabled,
    } = restaurantSetting;

    // Upload the logoImage of restaurant to server and save logoImage to restaurant setting modal
    let logoKey = null;

    // Upload the backgroundImage of restaurant to server and save backgroundImage to restaurant setting modal
    let backgroundImageKey = null;
    const folderName = `${id}-prod`;

    try {
      // Reset the loading status and error message
      setError((prevState) => ({
        ...prevState,
        postRestaurant: "",
      }));
      setLoadingStatus((prevState) => ({
        ...prevState,
        postRestaurant: true,
      }));

      // If have logo-image. Do upload to Storage
      if (logoImage) {
        const resultLogo = await Storage.put(
          folderName + "/logo.png",
          logoImage
        );

        // get the logoKey, it will use to render the logo-image
        logoKey = resultLogo.key;
      }

      // If have background image. Do upload to Storage
      if (backgroundImage) {
        const resultLogo = await Storage.put(
          folderName + "/backgroundImage.png",
          backgroundImage
        );

        // get the backgroundImageKey, it will use to render the background image
        backgroundImageKey = resultLogo.key;
      }

      // Generator the new restaurant input
      const restaurantInput = {
        userId: auth.user.id,
        groupName: auth.userGroup,
        name: values.restaurantName,
        introduction: values.restaurantDescription || null,
        displayDate,
        displayLogo,
        displayCurrencySymbol,
        logoKey: logoKey || restaurantSetting.logoKey,
        displayBackground,
        backgroundImageKey:
          backgroundImageKey || restaurantSetting.backgroundImageKey,
        displayLogoInQRCode,
        restaurantName,
        dateText,
        descriptionText,
        paidMenuImages: restaurantSetting.paidMenuImages ? true : false,
        bookmarksEnabled,
        zoomDisabled,
        loadSectionOnly,
      };

      // Checking restaurant was exist or not. If exist, do update restaurant setting
      if (id) {
        const updateRestaurant = await handleUpdateRestaurant({
          id,
          ...restaurantInput,
        });

        setLoadingStatus((prevState) => ({
          ...prevState,
          postRestaurant: false,
        }));

        if (updateRestaurant) {
          dispatch({
            type: UPDATE_RESTAURANT_SUCCESS,
            payload: updateRestaurant,
          });

          // Toggle the modal create-menu in case selected restaurant don't have any menu
          if (updateRestaurant.menus.items.length === 0) {
            dispatch({ type: TOGGLE_CREATE_MENU });
            return;
          }

          // Toggle the modal update-menu in case selected restaurant have 1 menu
          if (updateRestaurant.menus.items.length === 1) {
            dispatch({
              type: TOGGLE_UPDATE_MENU,
              data: updateRestaurant.menus.items[0].id,
            });
            return;
          }
        }

        /**
         * Redirect to "Yours Menu" section if have list menu before
         * Using setTimeout in this case to pass the  React router "Prompt" condition
         */
        setTimeout(() => history.push("/menus-list"), 0);
        return;
      }

      // If don't have any restaurant. Create the new one
      const createRestaurant = await handleCreateRestaurant(restaurantInput);
      dispatch({ type: CREATE_RESTAURANT_SUCCESS, payload: createRestaurant });
    } catch (error) {
      dispatch({
        type: id ? UPDATE_RESTAURANT_ERROR : CREATE_RESTAURANT_ERROR,
      });

      setError((prevState) => ({
        ...prevState,
        postRestaurant: error.message,
      }));
    } finally {
      // Reset loading status
      setLoadingStatus((prevState) => ({
        ...prevState,
        postRestaurant: false,
      }));
    }
  };

  function handleUploadImage(file) {
    if (file) {
      setRestaurantSetting((prevState) => ({
        ...prevState,
        displayLogo: true,
        logoImage: file,
      }));
    }
  }

  function handleSettingChange(property, value = "") {
    if (property === "bookmarksEnabled" && !restaurantSetting.paidBookmarks) {
      setError((prevState) => ({
        ...prevState,
        preventBookmarkUpdate: true,
      }));
    } else {
      setRestaurantSetting((prevState) => {
        if (property === "name" || property === "introduction") {
          return {
            ...prevState,
            [`${property}`]: value,
          };
        }

        return {
          ...prevState,
          [`${property}`]: !prevState[property],
        };
      });
    }
  }

  function handleUploadBgImage(file) {
    if (file) {
      setRestaurantSetting((prevState) => ({
        ...prevState,
        backgroundImage: file,
        displayBackground: true,
      }));
    }
  }

  function handleUpdateFontColor(currentState) {
    setRestaurantSetting((prevState) => ({
      ...prevState,
      ...currentState,
    }));
  }

  const settingPreviewStyle = useCallback(
    (type) => {
      const fontSetting = restaurantSetting[type];
      return {
        ...(fontSetting?.font && { fontFamily: fontSetting?.font }),
        ...(fontSetting?.color
          ? { color: fontSetting?.color }
          : { color: "#fff" }),
        ...(fontSetting?.textstyle &&
          fontSetting?.textstyle.includes("bold") && {
            fontWeight: "bold",
          }),
        ...(fontSetting?.textstyle &&
          fontSetting?.textstyle.includes("italic") && {
            fontStyle: "italic",
          }),
      };
    },
    [restaurantSetting]
  );

  function closePreviewModal() {
    setTogglePreviewModal(false);
  }
  function openPreviewModal() {
    setTogglePreviewModal(true);
  }

  return (
    <>
      <ResizablePreviewModal
        isDisplay={togglePreviewModal}
        handleClose={closePreviewModal}
      >
        <PreviewRestaurantBackgroundImage
          restaurantSetting={restaurantSetting}
          previewBackgroundImage={previewBackgroundImage}
          nameStyle={settingPreviewStyle("restaurantName")}
          descriptionTextStyle={settingPreviewStyle("descriptionText")}
          dateStyle={settingPreviewStyle("dateText")}
        />
      </ResizablePreviewModal>

      <Container fluid className="wrapper px-0">
        <Row className="justify-content-between mx-0 title-setting-wrapper">
          <div className="d-flex">
            <h3 className="mb-4 title-setting">Restaurant Settings</h3>
            {loadingStatus.getRestaurant && (
              <Spinner
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
                className="ml-1"
              />
            )}
          </div>
        </Row>
        <Row className="mx-0 px-4 setting-form">
          {error.getRestaurant ? (
            <Alert variant="danger" className="w-100">
              {error.getRestaurant}
            </Alert>
          ) : (
            <Formik
              enableReinitialize
              initialValues={{
                restaurantName: restaurantSetting.name || "",
                restaurantDescription: restaurantSetting.introduction || "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, setFieldValue }) => (
                <Form className="w-100">
                  <Prompt
                    when={!isEqual(restaurant, restaurantSetting)}
                    message="Changes you made may not be saved. Are you sure you want to leave?"
                  />
                  <ListGroup className="mb-4">
                    <ListGroup.Item>
                      <RestaurantSettingInputsMemo
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        handleSettingChange={handleSettingChange}
                      />

                      {/* Display date setting field */}
                      <SettingItem
                        className="mt-3"
                        title="Display Date"
                        property="displayDate"
                        value={restaurantSetting.displayDate}
                        handleClick={handleSettingChange}
                      />

                      {/* Display currency setting field */}
                      <SettingItem
                        className="mt-3"
                        title="Display Currency Symbol"
                        property="displayCurrencySymbol"
                        value={restaurantSetting.displayCurrencySymbol}
                        handleClick={handleSettingChange}
                      />

                      {/* Display currency setting field */}
                      <SettingItem
                        className="mt-3"
                        title="Enable Bookmarks"
                        property="bookmarksEnabled"
                        value={restaurantSetting.bookmarksEnabled}
                        handleClick={handleSettingChange}
                      />

                      {error.preventBookmarkUpdate && (
                        <div>
                          <Alert
                            variant="warning"
                            data-testid="preventEnableSettingUpdateError"
                          >
                            Please contact our support team to{" "}
                            <strong>
                              <a href="mailto: support@binwise.com">
                                support@binwise.com
                              </a>
                            </strong>{" "}
                            to use this feature!
                          </Alert>
                        </div>
                      )}
                    </ListGroup.Item>

                    <ListGroup.Item>
                      {/* Display logo setting field */}
                      <SettingItem
                        className="mt-3"
                        title="Display Logo"
                        property="displayLogo"
                        value={restaurantSetting.displayLogo}
                        handleClick={handleSettingChange}
                      >
                        <ImageUpload
                          logoKey={restaurantSetting?.logoKey}
                          handleUploadImage={handleUploadImage}
                        />
                      </SettingItem>

                      {/* Display logo QR-code setting field */}
                      <SettingItem
                        title="Display logo in QR code"
                        property="displayLogoInQRCode"
                        value={restaurantSetting?.displayLogoInQRCode}
                        handleClick={handleSettingChange}
                      />

                      {/* Display background setting field */}
                      <SettingItem
                        title="Background"
                        className="mt-3"
                        property="displayBackground"
                        value={restaurantSetting.displayBackground}
                        handleClick={handleSettingChange}
                        declinedBtnText="No"
                      />

                      {/* Display zoom setting field */}
                      <SettingItem
                        title="Disable Pinch Zoom on Menu"
                        className="mt-3"
                        property="zoomDisabled"
                        value={restaurantSetting?.zoomDisabled}
                        handleClick={handleSettingChange}
                      />

                      {/* Upload background setting field */}
                      <BackgroundImageUpload
                        handleUploadBgImage={handleUploadBgImage}
                        backgroundImageKey={
                          restaurantSetting.backgroundImageKey
                        }
                        setPreviewImage={setPreviewBackgroundImage}
                      />
                    </ListGroup.Item>

                    <ListGroup.Item>
                      {/* Setting text style  */}
                      <FontSelection
                        setting={restaurantSetting}
                        handleUpdateSetting={handleUpdateFontColor}
                      />

                      <div className="setting-item-title">
                        {`Preview background image ${
                          restaurantSetting?.displayBackground
                            ? previewBackgroundImage
                              ? ""
                              : "(default)"
                            : "(default)"
                        }`}
                      </div>
                      <PreviewRestaurantBackgroundImage
                        restaurantSetting={restaurantSetting}
                        previewBackgroundImage={previewBackgroundImage}
                        nameStyle={settingPreviewStyle("restaurantName")}
                        descriptionTextStyle={settingPreviewStyle(
                          "descriptionText"
                        )}
                        dateStyle={settingPreviewStyle("dateText")}
                      />
                      <div className="d-flex align-items-center justify-content-center py-4">
                        <Button
                          variant="outline-dark"
                          onClick={openPreviewModal}
                          className="preview-size-button"
                        >
                          Preview in different size
                        </Button>
                      </div>
                    </ListGroup.Item>
                  </ListGroup>
                  <hr />
                  {/* Form action */}
                  <ButtonToolbar
                    aria-label="Toolbar with button groups"
                    className="justify-content-center pt-2"
                  >
                    <ButtonGroup aria-label="Third group">
                      <Button
                        type="submit"
                        variant="dark"
                        className="dark-button"
                        disabled={loadingStatus.postRestaurant}
                      >
                        Continue
                        {loadingStatus.postRestaurant && (
                          <Spinner
                            as="div"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="ml-1"
                          />
                        )}
                      </Button>
                    </ButtonGroup>
                  </ButtonToolbar>
                  {error.postRestaurant && (
                    <Alert className="mt-2" variant="danger">
                      {error.postRestaurant}
                    </Alert>
                  )}
                </Form>
              )}
            </Formik>
          )}
        </Row>
      </Container>
    </>
  );
};

PreviewRestaurantBackgroundImage.propTypes = {
  restaurantSetting: PropTypes.object.isRequired,
  previewBackgroundImage: PropTypes.string,
  nameStyle: PropTypes.object.isRequired,
  descriptionTextStyle: PropTypes.object.isRequired,
  dateStyle: PropTypes.object.isRequired,
};

ResizablePreviewModal.propTypes = {
  isDisplay: PropTypes.bool.isRequired,
  children: PropTypes.element,
  handleClose: PropTypes.func.isRequired,
};

GeneralSettings.propTypes = {
  auth: PropTypes.object.isRequired,
  restaurant: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  getRestaurant: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  const { auth, restaurant } = state;

  return {
    auth,
    restaurant: restaurant.restaurant,
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators({ getRestaurant }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(GeneralSettings);
